import { Trash2, BookmarkPlus, Wand2 } from "lucide-react";
import { formatMoney } from "@/lib/format";
import { useApi } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import CategoryPicker from "@/components/ledger/CategoryPicker";

const cell = "h-8 w-full rounded-md bg-transparent px-2 text-sm border border-transparent hover:border-border focus:border-primary focus:outline-none";

export default function BankPreviewTable({ rows, setRows, categories, directors, onDelete, onSaveRule }) {
  const api = useApi();
  const set = (ref, patch) => setRows((list) => list.map((r) => (r.ref === ref ? { ...r, ...patch } : r)));
  // after editing a description, look the category up again unless you picked one yourself
  const rematch = async (r) => {
    if (r.manual || r.description === r.matchedFor) return;
    try {
      const res = await api("post", "/bank-feed/match", { items: [{ key: r.ref, side: r.received ? "in" : "out", description: r.description }] });
      set(r.ref, { category: res[r.ref], matched: !!res[r.ref], matchedFor: r.description });
    } catch { /* keep what's there */ }
  };
  const all = rows.every((r) => r.keep);
  return (
    <div className="border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="bank-preview-table">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="pl-4 py-3 w-8"><Checkbox checked={all} onCheckedChange={(v) => setRows((l) => l.map((r) => ({ ...r, keep: !!v })))} data-testid="bank-select-all" /></th>
            {["Date", "Description", "In", "Out", "Company or personal", "Category", "For", "What", "Project", ""].map((h, i) => (
              <th key={i} className={`px-2 py-3 font-medium ${h === "In" || h === "Out" ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ref} className={`border-b border-border last:border-0 ${r.keep ? "" : r.repayment ? "opacity-70" : "opacity-40"} ${r.personal_director ? "bg-indigo-500/[0.06]" : !r.category ? "bg-amber-500/[0.06]" : ""}`} data-testid={`bank-row-${r.ref}`}>
              <td className="pl-4"><Checkbox checked={r.keep} onCheckedChange={(v) => set(r.ref, { keep: !!v })} data-testid={`bank-keep-${r.ref}`} /></td>
              <td className="px-2 font-mono text-xs whitespace-nowrap">
                {r.date}
                <div className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`bank-account-tag-${r.ref}`}>{r.bank_account || "CommBank"}</div>
              </td>
              <td className="px-1 min-w-[260px]">
                <input className={`${cell} border-border/60`} value={r.description} onChange={(e) => set(r.ref, { description: e.target.value })}
                  onBlur={() => rematch(r)} data-testid={`bank-description-${r.ref}`} />
                {r.repayment && (
                  <div className="px-2 text-[11px] text-sky-600" data-testid={`bank-repayment-${r.ref}`}>Card repayment · left unticked so it isn't counted twice</div>
                )}
                {r.original && r.original !== r.description && (
                  <div className="px-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                    {r.renamed && r.description === r.renamedTo && <span className="inline-flex items-center gap-1 text-primary" data-testid={`bank-rule-applied-${r.ref}`}><Wand2 className="h-3 w-3" />rule</span>}
                    <span className="truncate max-w-[260px]" title={r.original}>Bank: {r.original}</span>
                    {!(r.renamed && r.description === r.renamedTo) && (
                      <button type="button" onClick={() => onSaveRule(r)} className="inline-flex items-center gap-1 text-primary hover:underline shrink-0" data-testid={`bank-save-rule-${r.ref}`}>
                        <BookmarkPlus className="h-3 w-3" />Save as rule
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td className="px-2 text-right font-mono text-emerald-400">{formatMoney(r.received)}</td>
              <td className="px-2 text-right font-mono text-rose-400">{formatMoney(r.spent)}</td>
              <td className="px-1 w-44">
                <select value={r.personal_director || ""} onChange={(e) => set(r.ref, { personal_director: e.target.value || null })}
                  className={`${cell} border-border bg-background`} data-testid={`bank-personal-${r.ref}`}>
                  <option value="">Company</option>
                  {directors.map((d) => <option key={d.id} value={d.id}>Personal · {d.name}</option>)}
                </select>
                {r.personal_director && (
                  <div className="px-2 text-[11px] text-indigo-500">{r.spent ? "Debit" : "Credit"} to {directors.find((d) => d.id === r.personal_director)?.name}</div>
                )}
              </td>
              <td className="px-1 w-48">
                {r.personal_director ? <span className="px-2 text-xs text-muted-foreground">Not needed</span> : (
                  <CategoryPicker value={r.category} categories={categories} testId={`bank-category-${r.ref}`} onChange={(v) => set(r.ref, { category: v, matched: false, manual: true })} />
                )}
                {r.matched && !r.personal_director && <div className="px-2 text-[11px] text-emerald-500" data-testid={`bank-matched-${r.ref}`}>from past rows</div>}
              </td>
              {["for_whom", "what", "project"].map((k) => (
                <td key={k} className="min-w-[110px]"><input className={cell} value={r[k] || ""} onChange={(e) => set(r.ref, { [k]: e.target.value })} data-testid={`bank-${k.replace("_", "-")}-${r.ref}`} /></td>
              ))}
              <td className="pr-3">
                <button type="button" onClick={() => onDelete(r.ref)} title="Delete this line — it won't come back on future uploads"
                  className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 transition-colors" data-testid={`bank-delete-${r.ref}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
